/**
 * usePurchaseInvoiceCalculations Hook
 * Alış faturası hesaplama işlemleri (Toplam, KDV, Tevkifat, Stopaj)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { InvoiceItem } from '../components/types/invoice.types';

interface UsePurchaseInvoiceCalculationsProps {
  selectedItems: InvoiceItem[];
  tevkifatRate: string;
  stopajRate: number;
  originalInvoiceTotal: string;
}

export const usePurchaseInvoiceCalculations = ({
  selectedItems,
  tevkifatRate,
  stopajRate,
  originalInvoiceTotal,
}: UsePurchaseInvoiceCalculationsProps) => {
  // Toplam değerler
  const [subtotal, setSubtotal] = useState<number>(0);
  const [netSubtotal, setNetSubtotal] = useState<number>(0);
  const [vatTotal, setVatTotal] = useState<number>(0);
  const [sctTotal, setSctTotal] = useState<number>(0);
  const [discountTotal, setDiscountTotal] = useState<number>(0);
  const [grandTotal, setGrandTotal] = useState<number>(0);
  const [tevkifatAmount, setTevkifatAmount] = useState<number>(0);
  const [stopajAmount, setStopajAmount] = useState<number>(0);
  const [vatRateTotals, setVatRateTotals] = useState<{[key: number]: number}>({});
  const [invoiceDifference, setInvoiceDifference] = useState<number>(0);
  
  // Toplam değerleri hesaplama
  useEffect(() => {
    const calculateTotals = () => {
      let subTotal = 0;
      let vatTotalAmount = 0;
      let sctTotalAmount = 0;
      let discountTotalAmount = 0;
      
      // KDV oranlarına göre toplamaları tutacak nesne
      const vatRateAmounts: {[key: number]: number} = {};
      
      selectedItems.forEach(item => {
        subTotal += item.lineTotal;
        vatTotalAmount += item.vatAmount;
        sctTotalAmount += item.sctAmount;
        discountTotalAmount += (item.lineTotal * item.discountRate / 100);
        
        // KDV oranına göre tutarları topla
        const vatRate = Math.round(item.vatRate);
        if (vatRateAmounts[vatRate]) {
          vatRateAmounts[vatRate] += item.vatAmount;
        } else {
          vatRateAmounts[vatRate] = item.vatAmount;
        }
      });
      
      // Tevkifat tutarını hesapla
      let tevkifatValue = 0;
      if (tevkifatRate !== "0") {
        const [numerator, denominator] = tevkifatRate.split('/').map(Number);
        const tevkifatRatio = numerator / denominator;
        tevkifatValue = vatTotalAmount * tevkifatRatio;
      }
      setTevkifatAmount(tevkifatValue);
      
      // Stopaj tutarını hesapla - KDV üzerinden
      const stopajValue = (stopajRate > 0) ? (vatTotalAmount * stopajRate / 100) : 0;
      setStopajAmount(stopajValue);
      
      // Genel toplam = Alt Toplam + KDV - Tevkifat - Stopaj + ÖTV - İndirim
      const grandTotalAmount = subTotal + vatTotalAmount - tevkifatValue - stopajValue + sctTotalAmount - discountTotalAmount;
      
      setSubtotal(subTotal);
      setNetSubtotal(subTotal - discountTotalAmount);
      setVatTotal(vatTotalAmount);
      setSctTotal(sctTotalAmount);
      setDiscountTotal(discountTotalAmount);
      setGrandTotal(grandTotalAmount);
      setVatRateTotals(vatRateAmounts);
    };
    
    calculateTotals();
  }, [selectedItems, tevkifatRate, stopajRate]);
  
  // Fatura farkını hesapla (Orijinal Tutar - Hesaplanan Tutar)
  useEffect(() => {
    if (originalInvoiceTotal && originalInvoiceTotal.trim() !== '') {
      // Virgül ve noktaları temizle, sayıya çevir
      const cleanedValue = originalInvoiceTotal.replace(/\./g, '').replace(',', '.');
      const originalValue = parseFloat(cleanedValue);
      
      if (!isNaN(originalValue) && originalValue > 0) {
        const difference = originalValue - grandTotal;
        setInvoiceDifference(difference);
      } else {
        setInvoiceDifference(0);
      }
    } else {
      setInvoiceDifference(0);
    }
  }, [originalInvoiceTotal, grandTotal]);
  
  // Final toplam (orijinal tutar varsa farkı dahil et)
  const finalGrandTotal = useMemo(() => {
    if (originalInvoiceTotal && originalInvoiceTotal.trim() !== '') {
      return grandTotal + invoiceDifference;
    }
    return grandTotal;
  }, [grandTotal, originalInvoiceTotal, invoiceDifference]);
  
  return {
    subtotal,
    netSubtotal,
    // KDV hariç matrah indirimi = Ara Toplam − İndirim Sonrası Ara Toplam (özet için)
    discountNetTotal: discountTotal,
    vatTotal,
    sctTotal,
    discountTotal,
    grandTotal,
    tevkifatAmount,
    stopajAmount,
    vatRateTotals,
    invoiceDifference,
    finalGrandTotal,
    
    // Setters (gerekirse dışarıdan değiştirmek için)
    setTevkifatAmount,
    setStopajAmount,
  };
};
